import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Button, CircularProgress, IconButton, Popover, Stack, Typography, Tooltip as MuiTooltip, css } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceArea } from 'recharts';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { NameType, Payload, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import PendingIcon from '@mui/icons-material/PendingOutlined';

import { GetFrequencySpectrumRequest, useGetFrequencySpectrum } from './attributes/api/useSeismicController';
import { useSeismicStore } from '../stores/useSeismicStore';
import { useMapStore } from '../stores/useMapStore';
import { FrequencySpectrum } from '../models/types/FrequencySpectrum';
import { FrequencySpectrumList } from '../models/types/FrequencySpectrumList';

export type FrequencySpectrumChartProps = {
    freqSpectrumLists: FrequencySpectrumList[],
    isLoading?: boolean,
};

export const FrequencySpectrumChart = ({ freqSpectrumLists, isLoading = false } : FrequencySpectrumChartProps) => {
    const getAmplitudeRegisterKey = useCallback((startTrace: number, endTrace: number, startSample: number, endSample: number) => {
        return `trace_${startTrace}_${endTrace}_sample_${startSample}_${endSample}`;
    }, []);

    const chartData = useMemo(() => {
        const data: unknown[] = [];
        freqSpectrumLists.forEach(freqSpectrumList => {
            const key = `${getAmplitudeRegisterKey(freqSpectrumList.startTrace, freqSpectrumList.endTrace, freqSpectrumList.startSample, freqSpectrumList.endSample)}`;
            data.push(...(freqSpectrumList.frequencySpectrums.map(freqSpectrum => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const obj : {[key: string]: any} = {};
                obj[key] = freqSpectrum.AmplitudePercentile;
                obj.frequency = freqSpectrum.Frequency;
                return obj;
            })));
        });
        return data;
    }, [freqSpectrumLists, getAmplitudeRegisterKey]);

    const [helpAnchorEl, setHelpAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const helpAnchorElRef = useRef<HTMLButtonElement|null>(null);

    const [ xAxisDomain, setXAxisDomain ] = useState<[number, number]>([0, 0]);

    const [ refLeftLimit, setRefLeftLimit ] = useState<number|null>(null);
    const [ refRightLimit, setRefRightLimit ] = useState<number|null>(null);

    const zoom = useCallback(() => {
        // xAxis domain
        let leftRef = refLeftLimit;
        let rightRef = refRightLimit;

        if (leftRef !== rightRef && !!leftRef && !!rightRef) {
            setXAxisDomain([Math.round(Math.min(leftRef, rightRef)), Math.round(Math.max(rightRef, leftRef))]);
        }

        setRefLeftLimit(null);
        setRefRightLimit(null);

    }, [refLeftLimit, refRightLimit]);

    const loadingIconCss = css({
        '& > *': {
            animation: 'fade 2s linear',
            animationIterationCount: 'infinite'
        },
        '@keyframes fade': {
            '0%,100%': { opacity: 0},
            '50%': { opacity: 1}
        }
    });

    const resetZoom = useCallback(() => {
        let min = 0;
        let max = 40;

        freqSpectrumLists.forEach(freqSpectrumList =>
            freqSpectrumList.frequencySpectrums.forEach(freqSpectrum =>
            {
                const freq = freqSpectrum.Frequency;
                if (freq > max) {
                    max = freq;
                }
                if (freq < min) {
                    min = freq;
                }
            }
            )
        );

        setXAxisDomain([Math.round(min), Math.round(max)]);
    }, [freqSpectrumLists]);

    useEffect(() => {
        let max = 0;
        let min = 0;

        freqSpectrumLists.forEach(freqSpectrumLists => {
            freqSpectrumLists.frequencySpectrums.forEach(freqSpectrum => {
                const frequency = freqSpectrum.Frequency;
                if (frequency > max) {
                    max = frequency;
                };
                if (frequency < min) {
                    min = frequency;
                }
            });
        });

        if (xAxisDomain[0] === 0 && xAxisDomain[1] === 0) {
            setXAxisDomain([Math.round(min), Math.round(max)]);
        }
    }, [freqSpectrumLists]);

    useEffect(() => {
        setHelpAnchorEl(helpAnchorElRef.current);
    }, []);

    return (
        <Stack direction='column' width="100%" height={350} sx={{userSelect: 'none'}}>
            <ResponsiveContainer width='100%' height="100%">
                <LineChart
                    data={chartData}
                    onMouseDown={(evt) => setRefLeftLimit(!!evt.activeLabel ? parseFloat(evt.activeLabel) : null)}
                    onMouseMove={(evt) => !!refLeftLimit && setRefRightLimit(!!evt.activeLabel ? parseFloat(evt.activeLabel) : null)}
                    onMouseUp={zoom}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        allowDataOverflow
                        dataKey="frequency"
                        type='number'
                        label={{ value: 'Frequency (Hz)', position: 'insideTop', offset: -5 }}
                        domain={xAxisDomain}
                        orientation='top'
                    />
                    <YAxis allowDataOverflow type='number' label={{ value: 'Amplitude (%)', angle: -90, position: 'insideLeft' }} />
                    <Tooltip content={<CustomTooltip/>}/>
                    {
                        freqSpectrumLists.map(freqSpectrumList =>
                            <Line
                                stroke={freqSpectrumList.color}
                                key={getAmplitudeRegisterKey(freqSpectrumList.startTrace, freqSpectrumList.endTrace, freqSpectrumList.startSample, freqSpectrumList.endSample)}
                                type='natural'
                                dataKey={getAmplitudeRegisterKey(freqSpectrumList.startTrace, freqSpectrumList.endTrace, freqSpectrumList.startSample, freqSpectrumList.endSample)}
                                dot={false}
                                animationDuration={300}
                            />)
                    }
                    {
                        !!refLeftLimit && !!refRightLimit &&
                        <ReferenceArea x1={refLeftLimit} x2={refRightLimit} strokeOpacity={0.3}/>
                    }
                </LineChart>
            </ResponsiveContainer>
            <Stack direction='row-reverse' maxHeight={'50px'} gap={1} sx={theme => ({ [theme.breakpoints.down('xl')]: { flexDirection: 'row' } })}>
                <IconButton size='large' onClick={resetZoom}> <ZoomOutIcon color='primary'/> </IconButton>
                <IconButton size='large' onClick={(evt) => setHelpAnchorEl(evt.currentTarget)} ref={helpAnchorElRef}> <QuestionMarkIcon color='primary'/> </IconButton>
                <Popover
                    anchorEl={helpAnchorEl}
                    open={!!helpAnchorEl}
                    onClose={() => setHelpAnchorEl(null)}
                    anchorOrigin={{
                        horizontal: 'center',
                        vertical: 'bottom'
                    }}
                >
                    <Box width='200px' padding='10px'>
                        <Typography>Hold Ctrl, then click and drag to select a line segment and view its frequency spectrum.</Typography>
                    </Box>
                </Popover>
                {
                    isLoading
                    &&
                    <MuiTooltip title='Loading...' >
                        <IconButton css={loadingIconCss}>
                            <PendingIcon color='primary'/>
                        </IconButton>
                    </MuiTooltip>
                }

            </Stack>
        </Stack>
    );
};

type CustomTooltipProps = {
    active?: boolean,
    payload?: Payload<ValueType, NameType>[],
    label?: string
};

const CustomTooltip = ({ active, label, payload }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
        return (
            <Stack padding='10px 5px' sx={{backgroundColor: 'white', border: '1px solid black'}}>
                <Typography>{!!label && parseFloat(label).toFixed(2)}</Typography>
            </Stack>
        );
    }
    else {
        return (<Stack></Stack>);
    }
} ;